import { useState, useRef } from "react";
import { toast } from "sonner";
import {
  Heart, Brain, BookOpen, Users, Briefcase, Target,
  Sparkles, Sun, Moon, Volume2, VolumeX,
  ChevronDown, MoreVertical, Crown, Palette, Lock, Bookmark, Settings, Share2, Download, Tag,
  Flame, Link2, Menu,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { WisdomFigureBadge, RoundtableBadge } from "@/components/WisdomCouncil";
import { UsageCounter } from "@/components/UpgradePrompt";
import { ConversationInsights } from "@/components/ConversationInsights";
import { ChatSummarizer } from "@/components/ChatSummarizer";
import { TagMenu } from "@/components/TagMenu";

const MODULES = [
  { key: "friend", name: "Friend", icon: Heart, color: "text-pink-500" },
  { key: "psychologist", name: "Psychologist", icon: Brain, color: "text-blue-500" },
  { key: "mentor", name: "Mentor", icon: BookOpen, color: "text-amber-600" },
  { key: "social_guide", name: "Social", icon: Users, color: "text-green-500" },
  { key: "job_guardian", name: "Guardian", icon: Briefcase, color: "text-purple-500" },
  { key: "life_coach", name: "Coach", icon: Target, color: "text-orange-500" },
  { key: "storyteller", name: "Storyteller", icon: Sparkles, color: "text-teal-500" },
];

const EXCLUSIVE_MODULES = [
  { key: "philosopher_king", name: "Philosopher King", icon: Crown, color: "text-violet-500", tier: "diamond" },
  { key: "creative_muse", name: "Creative Muse", icon: Palette, color: "text-rose-500", tier: "diamond" },
  { key: "executive_advisor", name: "Executive Advisor", icon: Briefcase, color: "text-indigo-500", tier: "diamond" },
];

export { MODULES, EXCLUSIVE_MODULES };

export function ChatHeader({
  // Module
  module, setModule, showModuleMenu, setShowModuleMenu,
  // Wisdom
  activeWisdomFigure, setActiveWisdomFigure,
  roundtableFigures, setRoundtableFigures, setRoundtableMode,
  setShowWisdomCouncil,
  // TTS
  ttsEnabled, toggleTts, speaking, stopSpeaking, ttsStatus,
  // Usage
  usageStatus, streak,
  // Settings/Actions
  setShowSavedItems, setShowMemoryPanel, setShowQuickSettings,
  darkMode, toggleDarkMode,
  // Conv actions
  activeConv, messages,
  showConvActions, setShowConvActions,
  showTagMenu, setShowTagMenu,
  setShowConvShare, setShowExportShare, exportConversation,
  // Tags
  allTags, convTags, tagColors, toggleConvTag, createTag, deleteTag,
  // User
  user, tier,
  // Feature gates
  psychologistEnabled,
  // Refs
  moduleMenuRef, convActionsRef, insightsRef,
  // i18n
  t,
  // Mobile
  setMobileOpen,
}) {
  const freeModules = ["friend", "psychologist_basic"];
  const isModuleLocked = (mod) => tier !== "free" ? false : !freeModules.includes(mod);

  return (
    <div className="border-b border-border/50 bg-card/30 px-2 sm:px-4 py-1.5 sm:py-2 flex items-center gap-1 sm:gap-2 min-h-0 overflow-visible">
      {/* Mobile Menu Toggle */}
      {setMobileOpen && (
        <button
          data-testid="mobile-chat-menu"
          onClick={() => setMobileOpen(true)}
          className="md:hidden flex items-center justify-center w-8 h-8 rounded-xl text-muted-foreground hover:bg-muted/50 transition-all flex-shrink-0"
        >
          <Menu className="w-4.5 h-4.5" strokeWidth={1.5} />
        </button>
      )}
      {/* Module Selector */}
      <div className="relative flex-shrink-0" ref={moduleMenuRef}>
        <button
          data-testid="module-selector"
          onClick={() => setShowModuleMenu(v => !v)}
          className="flex items-center gap-1 px-1.5 py-1 sm:px-2.5 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-semibold border-2 border-primary bg-primary/5 whitespace-nowrap max-w-[120px] sm:max-w-none"
        >
          {(() => { const m = [...MODULES, ...EXCLUSIVE_MODULES].find(m => m.key === module); return m ? <m.icon className={cn("w-3 h-3 sm:w-3.5 sm:h-3.5", m.color)} strokeWidth={1.5} /> : null; })()}
          <span className="truncate">{[...MODULES, ...EXCLUSIVE_MODULES].find(m => m.key === module)?.name || "Friend"}</span>
          <ChevronDown className={cn("w-2.5 h-2.5 sm:w-3 sm:h-3 text-muted-foreground flex-shrink-0 transition-transform", showModuleMenu && "rotate-180")} />
        </button>
      </div>
      {showModuleMenu && (
        <ModuleDropdown
          module={module} setModule={setModule}
          setShowModuleMenu={setShowModuleMenu}
          moduleMenuRef={moduleMenuRef}
          isModuleLocked={isModuleLocked}
          psychologistEnabled={psychologistEnabled}
          user={user}
          t={t}
        />
      )}

      {/* Wisdom Badge */}
      {activeWisdomFigure && roundtableFigures.length === 0 && (
        <WisdomFigureBadge figure={activeWisdomFigure} onClear={() => setActiveWisdomFigure(null)} />
      )}

      {/* Roundtable Badge */}
      {roundtableFigures.length > 1 && (
        <RoundtableBadge figures={roundtableFigures} onClear={() => { setRoundtableFigures([]); setRoundtableMode(false); }} />
      )}

      {/* Wisdom Council Button */}
      <button
        data-testid="wisdom-council-btn"
        onClick={() => { setRoundtableMode(false); setShowWisdomCouncil(true); }}
        className="flex items-center gap-1 px-1.5 py-1 sm:px-2 sm:py-1 rounded-full text-[10px] sm:text-[11px] font-medium bg-amber-400/15 border border-amber-300/30 dark:border-amber-700/30 text-amber-700 dark:text-amber-300 hover:bg-amber-400/25 transition-all flex-shrink-0"
      >
        <Sparkles className="w-3 h-3" strokeWidth={2} />
        <span className="hidden sm:inline">Wisdom</span>
      </button>

      {/* Roundtable Button */}
      <button
        data-testid="roundtable-btn"
        onClick={() => { setRoundtableMode(true); setShowWisdomCouncil(true); }}
        className={cn(
          "flex items-center gap-1 px-1.5 py-1 sm:px-2 sm:py-1 rounded-full text-[10px] sm:text-[11px] font-medium border transition-all flex-shrink-0",
          roundtableFigures.length > 1
            ? "bg-violet-400/20 border-violet-300/40 dark:border-violet-700/30 text-violet-700 dark:text-violet-300"
            : "bg-violet-400/10 border-violet-300/20 dark:border-violet-800/30 text-violet-600 dark:text-violet-400 hover:bg-violet-400/20"
        )}
      >
        <Users className="w-3 h-3" strokeWidth={2} />
        <span className="hidden sm:inline">Roundtable</span>
      </button>

      <div className="flex-1" />

      {/* Streak */}
      {streak > 0 && (
        <div data-testid="streak-counter" className="hidden sm:flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 flex-shrink-0">
          <Flame className="w-3.5 h-3.5 text-primary" strokeWidth={2} />
          <span className="text-xs font-semibold text-primary">{streak}</span>
        </div>
      )}

      {/* Usage Counter */}
      <UsageCounter
        messagesRemaining={usageStatus?.messages_remaining ?? -1}
        messagesLimit={usageStatus?.messages_limit ?? -1}
        unlimited={usageStatus?.unlimited_messages}
      />

      {/* TTS Toggle — with usage indicator */}
      <button
        data-testid="tts-toggle-btn"
        onClick={() => { toggleTts(); if (speaking) stopSpeaking(); }}
        className={cn(
          "relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold transition-all flex-shrink-0 border",
          ttsEnabled
            ? "bg-emerald-100 dark:bg-emerald-900/30 border-emerald-300/50 dark:border-emerald-700/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-900/50"
            : "bg-red-100 dark:bg-red-900/25 border-red-300/50 dark:border-red-700/40 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/40"
        )}
        title={ttsEnabled
          ? `Voice is ON${ttsStatus?.regular?.enabled ? " (Unlimited)" : " (Wisdom only)"}${ttsStatus?.wisdom?.unlimited ? "" : ` — ${ttsStatus?.wisdom?.remaining ?? "?"} wisdom left`}`
          : "Voice is OFF — click to unmute"
        }
      >
        {ttsEnabled ? (
          <>
            <Volume2 className={cn("w-4 h-4", speaking && "animate-pulse")} strokeWidth={2} />
            <span className="hidden sm:inline">{speaking ? "Speaking..." : "Voice On"}</span>
            {ttsStatus && !ttsStatus.regular?.enabled && !ttsStatus.is_unlimited_tier && (
              <span className="hidden sm:inline text-[9px] opacity-70 ml-0.5">(Wisdom)</span>
            )}
          </>
        ) : (
          <>
            <VolumeX className="w-4 h-4" strokeWidth={2} />
            <span className="hidden sm:inline">Muted</span>
          </>
        )}
        {speaking && ttsEnabled && (
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
        )}
      </button>

      <button data-testid="saved-items-btn" onClick={() => setShowSavedItems(true)}
        className="p-1.5 rounded-full hover:bg-muted/50 text-muted-foreground hover:text-primary transition-all flex-shrink-0" title="Saved items">
        <Bookmark className="w-3.5 h-3.5" />
      </button>

      <button data-testid="memory-btn" onClick={() => setShowMemoryPanel(true)}
        className="p-1.5 rounded-full hover:bg-muted/50 text-muted-foreground hover:text-primary transition-all flex-shrink-0" title="Omni's memory">
        <Brain className="w-3.5 h-3.5" />
      </button>

      <button data-testid="quick-settings-btn" onClick={() => setShowQuickSettings(true)}
        className="p-1.5 rounded-full hover:bg-muted/50 text-muted-foreground hover:text-primary transition-all flex-shrink-0" title="Quick settings">
        <Settings className="w-3.5 h-3.5" />
      </button>

      <button data-testid="dark-mode-toggle" onClick={toggleDarkMode}
        className="p-1.5 rounded-full hover:bg-muted/50 text-muted-foreground transition-all flex-shrink-0">
        {darkMode ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
      </button>

      {/* Conversation Actions Overflow */}
      {activeConv && (
        <div className="relative" ref={convActionsRef}>
          <button data-testid="conv-actions-btn" onClick={() => setShowConvActions(v => !v)}
            className="p-1.5 rounded-full hover:bg-muted/50 text-muted-foreground transition-all flex-shrink-0">
            <MoreVertical className="w-4 h-4" />
          </button>
          {showConvActions && (
            <div className="absolute right-0 top-full mt-1 bg-card border border-border/50 rounded-2xl shadow-xl z-40 p-1.5 min-w-[170px] animate-in fade-in slide-in-from-top-1 duration-150">
              <button onClick={() => { setShowConvShare(true); setShowConvActions(false); }} className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-xs hover:bg-muted/50"><Share2 className="w-3.5 h-3.5" /> Share</button>
              <button data-testid="share-pdf-link-btn" onClick={() => { setShowExportShare(true); setShowConvActions(false); }} className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-xs hover:bg-muted/50"><Link2 className="w-3.5 h-3.5" /> Share PDF Link</button>
              <button onClick={() => { exportConversation("pdf"); setShowConvActions(false); }} className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-xs hover:bg-muted/50"><Download className="w-3.5 h-3.5" /> Export PDF</button>
              <button onClick={() => { exportConversation("text"); setShowConvActions(false); }} className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-xs hover:bg-muted/50"><Download className="w-3.5 h-3.5" /> Export TXT</button>
              <button onClick={() => { setShowTagMenu(!showTagMenu); setShowConvActions(false); }} className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-xs hover:bg-muted/50"><Tag className="w-3.5 h-3.5" /> Tags</button>
              <span ref={insightsRef}><ConversationInsights conversationId={activeConv} messageCount={messages.length} /></span>
              <span><ChatSummarizer conversationId={activeConv} messageCount={messages.length} /></span>
            </div>
          )}
          {showTagMenu && <TagMenu allTags={allTags} convTags={convTags} tagColors={tagColors} onToggle={toggleConvTag} onCreate={createTag} onDelete={deleteTag} onClose={() => setShowTagMenu(false)} />}
        </div>
      )}
    </div>
  );
}

function ModuleDropdown({ module, setModule, setShowModuleMenu, moduleMenuRef, isModuleLocked, psychologistEnabled, user, t }) {

  return (
    <>
      <div className="fixed inset-0 z-[70]" onClick={() => setShowModuleMenu(false)} />
      <div
        data-testid="module-dropdown-menu"
        className="fixed bg-card border border-border/50 rounded-2xl shadow-2xl z-[71] p-1.5 min-w-[220px] max-h-[70vh] overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-150"
        style={{ top: moduleMenuRef.current ? moduleMenuRef.current.getBoundingClientRect().bottom + 4 : 50, left: moduleMenuRef.current ? Math.max(8, moduleMenuRef.current.getBoundingClientRect().left) : 8 }}
      >
        {MODULES.filter(({ key }) => key !== "psychologist" || psychologistEnabled !== false).map(({ key, name: mName, icon: Icon, color }) => {
          const locked = isModuleLocked(key);
          return (
            <button
              key={key}
              data-testid={`module-${key}`}
              onClick={() => { if (locked) { toast.error(`${mName} requires Omni+ subscription`); return; } setModule(key); setShowModuleMenu(false); }}
              className={cn(
                "flex items-center gap-2 w-full px-3 py-2 rounded-xl text-xs transition-all",
                module === key ? "bg-primary/10 font-semibold" : locked ? "text-muted-foreground/50" : "hover:bg-muted/50"
              )}
            >
              <Icon className={cn("w-4 h-4", module === key ? color : "text-muted-foreground")} strokeWidth={1.5} />
              <span className="flex-1 text-left">{mName}</span>
              {locked && <span className="text-[9px] bg-muted px-1.5 py-0.5 rounded-full">PRO</span>}
            </button>
          );
        })}
        {/* Exclusive Diamond modes */}
        <div className="border-t border-border/30 mt-1.5 pt-1.5">
          <p className="px-3 py-1 text-[9px] font-semibold text-violet-500 uppercase tracking-wider flex items-center gap-1"><Lock className="w-2.5 h-2.5" />Exclusive Modes</p>
          {EXCLUSIVE_MODULES.map(({ key, name: mName, icon: Icon, color }) => {
            const addonModes = user?.active_addon === "extras_full" ? ["philosopher_king", "creative_muse", "executive_advisor"] : user?.active_addon === "extras_limited" ? ["creative_muse", "executive_advisor"] : [];
            const hasAccess = ["diamond", "team_dev"].includes(user?.tier) || addonModes.includes(key);
            return (
              <button
                key={key}
                data-testid={`module-${key}`}
                onClick={() => { if (!hasAccess) { toast.error(`${mName} requires an Extras add-on or Platinum Plan`); return; } setModule(key); setShowModuleMenu(false); }}
                className={cn(
                  "flex items-center gap-2 w-full px-3 py-2 rounded-xl text-xs transition-all",
                  module === key ? "bg-violet-50 dark:bg-violet-950/30 font-semibold" : !hasAccess ? "text-muted-foreground/40" : "hover:bg-muted/50"
                )}
              >
                <Icon className={cn("w-4 h-4", module === key ? color : "text-muted-foreground/50")} strokeWidth={1.5} />
                <span className="flex-1 text-left">{mName}</span>
                {!hasAccess && <span className="text-[8px] bg-violet-100 dark:bg-violet-950/50 text-violet-600 dark:text-violet-400 px-1.5 py-0.5 rounded-full font-medium">EXTRAS</span>}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
